import { connect } from 'react-redux';
import { createSelector, createStructuredSelector } from 'reselect';
import {
  nest,
  hierarchy,
  treemap,
  treemapSlice,
  scaleLinear,
  max,
} from 'd3';

import Component from './Component';

const margin = () => ({ left: 120, right: 40, bottom: 20 });

const computeChart = (
  set,
  keys = [],
  prefix,
  size,
  margin,
  valueScale,
  offset = [0, 0]
) => {
  const nesting = keys.reduce((nest, key) => nest.key(d => d[key]), nest());
  const values = nesting.entries(set);

  const root = hierarchy({ key: null, values }, d => d.values);
  root.sum(d => d.weighting);
  root.eachAfter(d => {
    d.percentage = d.value / root.value;
  });

  const treemapLayout = treemap();
  treemapLayout.round(true);

  treemapLayout.paddingInner(2);

  const sliceWidth = 7.5;
  treemapLayout.tile(treemapSlice).size([sliceWidth, size[1]])(root);
  if (prefix === 'after') {
    root.eachAfter(node => {
      node.x0 += size[0] - sliceWidth;
      node.x1 += size[0] - sliceWidth;
    });
  }

  const process = ({ data, value, percentage, x0, y0, x1, y1 }) => ({
    key: data.key,
    side: prefix,
    value,
    percentage,
    data,
    x0: x0 + offset[0],
    y0: y0 + offset[1],
    x1: x1 + offset[0],
    y1: y1 + offset[1],
  });

  const allNodes = root.descendants();
  const nodes = allNodes.filter(({ height }) => height === 1).map(node => ({
    ...process(node),
    children: node.children.map(process),
  }));

  const groupNodes = keys.length > 1
    ? allNodes.filter(({ depth }) => depth === 1).map(process)
    : [];

  return { nodes, groupNodes, root };
};

const gutter = state => state.gutter || 0;
const size = createSelector(
  [(_, props) => props.width,
   (_, props) => props.data],
  (width, data) =>
    [width, Math.min(2*width, // max height
      Math.max(width / 1.05, // min height
        38*Math.max(
          data.filter(o => o.before).length, 
          data.filter(o => o.after).length)))] 
);
const sideSize = createSelector(
  [size, gutter],
  (size, gutter) =>
    [(size[0] - gutter) / 2, size[1]]
);

const keyBy = (_, props) => props.keyBy;
const aggregateBy = (_, props) => props.aggregateBy;

const groupingKeys = createSelector(
  [keyBy, aggregateBy],
  (keyBy, aggregateBy) =>
    (aggregateBy === keyBy ? [keyBy] : [aggregateBy, keyBy])
);

const swaps = createSelector([(_, props) => props.data], data =>
  data.map(swap => {
    const before = swap.before || { ...swap.after, weighting: 0 };
    const after = swap.after || { ...swap.before, weighting: 0 };
    before.willExit = after.weighting === 0;
    return {
      key: swap.key,
      before: { ...before, key: swap.key },
      after: { ...after, key: swap.key },
    };
  })
);

const numTicks = () => 6;
const valueScale = createSelector(
  [swaps, sideSize, margin, numTicks],
  (swaps, size, margin, numTicks) =>
    scaleLinear()
      .domain([
        0,
        max(swaps, d => Math.max(d.before.weighting, d.after.weighting)),
      ])
      .nice(numTicks)
      .rangeRound([0, size[0] - (margin.left || 0) - (margin.right || 0)])
);

const beforeSwaps = createSelector([swaps], 
  swaps => swaps.map(({ before }) => before)
);
const before = createSelector(
  [beforeSwaps, groupingKeys, sideSize, margin, valueScale],
  (swaps, groupingKeys, size, margin, valueScale) =>
    computeChart(
      swaps,
      groupingKeys,
      'before',
      size,
      margin,
      valueScale
    )
);

const afterOffset = createSelector(
  [sideSize, gutter],
  (sideSize, gutter) =>
    [sideSize[0] + gutter, 0]
);
const afterSwaps = createSelector([swaps], 
  swaps => swaps.map(({ after }) => after)
);
const after = createSelector(
  [afterSwaps, groupingKeys, sideSize, margin, valueScale, afterOffset],
  (swaps, groupingKeys, size, margin, valueScale, afterOffset) =>
    computeChart(
      swaps,
      groupingKeys,
      'after',
      size,
      margin,
      valueScale,
      afterOffset
    )
);

const allNodes = createSelector([before, after], (before, after) => [
  ...before.nodes,
  ...after.nodes,
]);
const allGroupNodes = createSelector([before, after], (before, after) => [
  ...before.groupNodes,
  ...after.groupNodes,
]);

const mapStateToProps = createStructuredSelector({
  swaps,
  nodes: allNodes,
  groupNodes: allGroupNodes,
  size,
  sideSize,
  aggregateBy,
  margin,
  valueScale,
  afterOffset,
  numTicks,
  // labelInside: createSelector(layout, layout => layout === 'Treemap'),
  trailingTransitionKeys: createSelector([], () => ['x0', 'x1']),
});

const mapDispatchToProps = dispatch => ({});

export default connect(mapStateToProps, mapDispatchToProps)(Component);



// WEBPACK FOOTER //
// ./src/Flows/index.js